import React, { useState, useCallback, useRef } from 'react';
import { Popover, Box, Spinner, SpaceBetween } from '@amzn/awsui-components-core';
import { Employee } from '../../interfaces/interface';
import { EmployeeService } from '../../services/EmployeeService';
import { HyperBadge } from '../../../../generic-components/hyper-badge/HyperBadge';
import './EmployeePopoverCard.scss';

interface EmployeePopoverCardProps {
  loginName: string;
  children: React.ReactNode;
  renderWithPortal?: boolean;
}

/**
 * Wraps any trigger element (e.g. Avatar) and shows an employee detail popover on hover.
 * Lazy-fetches employee data from EmployeeService (DDB cache → PAPI) on first hover.
 */
export const EmployeePopoverCard: React.FC<EmployeePopoverCardProps> = ({ loginName, children, renderWithPortal = false }) => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hasFetched = useRef(false);

  const fetchEmployee = useCallback(async () => {
    if (hasFetched.current || !loginName) return;
    hasFetched.current = true;
    setIsLoading(true);
    setHasError(false);

    try {
      const result = await EmployeeService.getEmployeeByLogin(loginName);
      setEmployee(result);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [loginName]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box padding="s" textAlign="center">
          <Spinner size="normal" />
        </Box>
      );
    }

    if (hasError || !employee) {
      return (
        <Box padding="s">
          <SpaceBetween direction="vertical" size="xxxs">
            <Box fontWeight="bold">{loginName}</Box>
            <Box fontSize="body-s" color="text-body-secondary">
              {hasError ? 'Failed to load details' : 'No details available'}
            </Box>
          </SpaceBetween>
        </Box>
      );
    }

    return (
      <Box padding="xxxs">
        <HyperBadge employee={employee} />
      </Box>
    );
  };

  return (
    <Popover triggerType="custom" content={renderContent()} position="bottom" size="large" dismissButton={true} renderWithPortal={renderWithPortal}>
      <span className="employee-popover-trigger" onMouseEnter={fetchEmployee} onClick={fetchEmployee}>
        {children}
      </span>
    </Popover>
  );
};
